// Responsible Trade (Final Implementation Authority §10.3 · D-08 CLOSED 2026-09-13).
//
// The closed set is Owner-ratified DATA — docs/authority/ny/NY-OWNER-RULINGS-TRADES-WORDING-SCOPE-
// RATIFICATION-20260913.md §3: 19 labels including "Other (specify)". The default adjective is
// Qualified (19 NYCRR 197-5.1(k): further evaluation "by a qualified professional, tradesman, or
// service technician"); a credential is shown only where the trade is actually licensed in the
// property's jurisdiction and the product holds that authority — today only the NYS Licensed Mold
// Assessor (statewide, NYS DOL). Codes are storage keys and never change; the label lives in the
// dictionaries (src/i18n, `trade.*`). Free text is fail-closed: "Other" must carry the specific trade
// in `responsibleTradeOther`; "Contact a professional." / "qualified professional" are never options.
// A retired code stays readable (a stored finding keeps its trade) but is never offered again and
// prints its successor's label.
export type ResponsibleTrade =
  | 'licensedElectrician' // label: Qualified Electrician
  | 'licensedPlumbingProfessional' // label: Qualified Plumbing Professional
  | 'qualifiedHvacContractor'
  | 'structuralEngineer' // label: Professional Engineer (PE) — only on an inspector-confirmed structural concern
  | 'qualifiedRoofingContractor'
  | 'licensedPestProfessional' // label: Qualified Pest Control / WDI Professional (NYS DEC certification underneath)
  | 'qualifiedGeneralContractor' // label: Qualified Contractor
  | 'qualifiedMasonryContractor' // label: Qualified Mason / Masonry Contractor
  | 'qualifiedSidingContractor'
  | 'qualifiedWindowDoorContractor'
  | 'qualifiedGarageDoorTechnician' // label: Qualified Garage Door Company
  | 'qualifiedServiceTechnician' // 2026-09-13 (TW-6): appliance / equipment service; replaces the appliance technician
  | 'qualifiedApplianceTechnician' // RETIRED 2026-09-13 — readable, never offered; prints as the service technician
  | 'qualifiedChimneyProfessional'
  | 'qualifiedInsulationContractor'
  | 'qualifiedWaterproofingContractor'
  | 'qualifiedDrainageGradingContractor' // label: Qualified Landscaping / Drainage Contractor
  | 'qualifiedPavingContractor'
  | 'licensedMoldAssessor' // label: NYS Licensed Mold Assessor — only on an inspector-confirmed mold-related concern
  | 'other';

// The offered set, in picker order (19 incl. Other). Retired codes are not here.
export const RESPONSIBLE_TRADE_ORDER: readonly ResponsibleTrade[] = Object.freeze([
  'licensedElectrician',
  'licensedPlumbingProfessional',
  'qualifiedHvacContractor',
  'structuralEngineer',
  'qualifiedRoofingContractor',
  'licensedPestProfessional',
  'qualifiedGeneralContractor',
  'qualifiedMasonryContractor',
  'qualifiedSidingContractor',
  'qualifiedWindowDoorContractor',
  'qualifiedGarageDoorTechnician',
  'qualifiedServiceTechnician',
  'qualifiedChimneyProfessional',
  'qualifiedInsulationContractor',
  'qualifiedWaterproofingContractor',
  'qualifiedDrainageGradingContractor',
  'qualifiedPavingContractor',
  'licensedMoldAssessor',
  'other'
]);

// Retired code → its successor. A finding written with the retired code keeps it on disk; every
// display and every new suggestion uses the successor.
export const RETIRED_RESPONSIBLE_TRADES: Readonly<Partial<Record<ResponsibleTrade, ResponsibleTrade>>> = Object.freeze({
  qualifiedApplianceTechnician: 'qualifiedServiceTechnician'
});

// Trades an AI observation alone never earns (ratification §3 / §4 / §7): the inspector confirms the
// structural or mold-related concern; until then the row / subject default stands.
export const AI_GUARDED_TRADES: readonly ResponsibleTrade[] = Object.freeze(['structuralEngineer', 'licensedMoldAssessor']);

export function isResponsibleTrade(value: unknown): value is ResponsibleTrade {
  return (
    typeof value === 'string' &&
    ((RESPONSIBLE_TRADE_ORDER as readonly string[]).includes(value) || Object.prototype.hasOwnProperty.call(RETIRED_RESPONSIBLE_TRADES, value))
  );
}

export function isRetiredResponsibleTrade(value: ResponsibleTrade): boolean {
  return Object.prototype.hasOwnProperty.call(RETIRED_RESPONSIBLE_TRADES, value);
}

// The code to show or to write for a stored code: retired → successor, everything else unchanged.
export function canonicalResponsibleTrade(trade: ResponsibleTrade | null): ResponsibleTrade | null {
  if (trade === null) return null;
  return RETIRED_RESPONSIBLE_TRADES[trade] ?? trade;
}

// A trade selection is complete when a code is chosen and, for "Other", the trade is spelled out.
export function responsibleTradeComplete(trade: ResponsibleTrade | null, other: string): boolean {
  if (trade === null) return false;
  return trade !== 'other' || other.trim().length > 0;
}
